import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import API from "../services/AnalyticsReadAPI";
import { hasAccountPermission } from "../utility/accountPermissions";

const AnalyticsContext = createContext();

const ANALYTICS_STALE_MS = 60 * 1000;
const DEFAULT_ANALYTICS_WINDOW_DAYS = 30;

const formatDateForQuery = (date) => {
  const year = date.getUTCFullYear();
  const month = `${date.getUTCMonth() + 1}`.padStart(2, "0");
  const day = `${date.getUTCDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getQueryDateForLocalBoundary = (date, { endOfDay = false } = {}) => {
  const boundary = new Date(date);

  if (endOfDay) {
    boundary.setHours(23, 59, 59, 999);
  } else {
    boundary.setHours(0, 0, 0, 0);
  }

  return formatDateForQuery(boundary);
};

const getDefaultDateRange = () => {
  const now = new Date();
  const startDate = new Date(now);
  startDate.setDate(startDate.getDate() - DEFAULT_ANALYTICS_WINDOW_DAYS);

  return {
    startDate: getQueryDateForLocalBoundary(startDate),
    endDate: getQueryDateForLocalBoundary(now, { endOfDay: true }),
  };
};

const isFresh = (lastFetchedAt) =>
  Boolean(lastFetchedAt) && Date.now() - lastFetchedAt < ANALYTICS_STALE_MS;

const sortList = (list, attribute, ascending = true) => {
  if (!Array.isArray(list)) return [];

  return [...list].sort((a, b) => {
    if (a?.[attribute] < b?.[attribute]) {
      return ascending ? -1 : 1;
    }
    if (a?.[attribute] > b?.[attribute]) {
      return ascending ? 1 : -1;
    }
    return 0;
  });
};

const applySortPreference = (list, activeSort, defaultAttribute, defaultOrder) => {
  const attribute = activeSort?.attribute || defaultAttribute;
  const order = activeSort?.attribute ? activeSort.order : defaultOrder;
  return sortList(list, attribute, order);
};

export const useAnalytics = () => useContext(AnalyticsContext);

export const AnalyticsProvider = ({ children }) => {
  const { organizationId, userId, isAuthenticated, permissions } = useAuth();
  const accessScope = isAuthenticated && userId && organizationId &&
    hasAccountPermission(permissions, "viewAnalytics")
    ? JSON.stringify([userId, organizationId]) : null;
  const accessScopeRef = useRef(accessScope);
  accessScopeRef.current = accessScope;
  const [dataScope, setDataScope] = useState(null);
  const refreshTimerRef = useRef(null);
  const inFlightRequestRef = useRef(null);
  const activeQueryRef = useRef(null);
  const requestVersionRef = useRef(0);
  const dirtyRef = useRef(true);
  const lastFetchedAtRef = useRef(0);

  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  const [insights, setInsights] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [analyticsDirty, setAnalyticsDirty] = useState(true);
  const [lastFetchedAt, setLastFetchedAt] = useState(0);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsRefreshing, setAnalyticsRefreshing] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);

  const [activeInsightSort, setActiveInsightSort] = useState({
    attribute: "",
    order: true,
  });
  const [activeSessionSort, setActiveSessionSort] = useState({
    attribute: "",
    order: true,
  });

  const activeInsightSortRef = useRef(activeInsightSort);
  const activeSessionSortRef = useRef(activeSessionSort);

  useEffect(() => {
    activeInsightSortRef.current = activeInsightSort;
  }, [activeInsightSort]);

  useEffect(() => {
    activeSessionSortRef.current = activeSessionSort;
  }, [activeSessionSort]);

  const setDirtyState = useCallback((nextDirty) => {
    dirtyRef.current = nextDirty;
    setAnalyticsDirty(nextDirty);
  }, []);

  const setFetchedAtState = useCallback((nextFetchedAt) => {
    lastFetchedAtRef.current = nextFetchedAt;
    setLastFetchedAt(nextFetchedAt);
  }, []);

  const markAnalyticsDirty = useCallback(() => {
    setDirtyState(true);
  }, [setDirtyState]);

  useEffect(() => {
    requestVersionRef.current += 1;
    inFlightRequestRef.current = null;
    activeQueryRef.current = null;
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = null;
    setInsights(null);
    setSessions(null);
    setStatistics(null);
    setDataScope(null);
    setAnalyticsError(null);
    setAnalyticsLoading(false);
    setAnalyticsRefreshing(false);
    setDirtyState(true);
    setFetchedAtState(0);
  }, [accessScope, setDirtyState, setFetchedAtState]);

  const refreshAnalytics = useCallback(
    async ({ force = false } = {}) => {
      if (!accessScope || accessScopeRef.current !== accessScope || !dateRange?.startDate || !dateRange?.endDate) {
        return null;
      }

      const queryKey = JSON.stringify([accessScope, dateRange.startDate, dateRange.endDate]);
      const sameQuery = activeQueryRef.current === queryKey;

      if (sameQuery && !force && !dirtyRef.current && isFresh(lastFetchedAtRef.current)) {
        return null;
      }

      if (sameQuery && inFlightRequestRef.current) {
        return inFlightRequestRef.current;
      }

      const hasCachedData = sameQuery && lastFetchedAtRef.current > 0;
      if (!sameQuery) {
        setInsights(null);
        setSessions(null);
        setStatistics(null);
        setFetchedAtState(0);
      }
      activeQueryRef.current = queryKey;
      const requestVersion = ++requestVersionRef.current;
      const isCurrentRequest = () => accessScopeRef.current === accessScope && requestVersionRef.current === requestVersion;
      setAnalyticsError(null);
      setAnalyticsLoading(!hasCachedData);
      setAnalyticsRefreshing(hasCachedData);

      const request = Promise.all([
        API.get(
          `questions?organization=${organizationId}&start=${dateRange.startDate}&end=${dateRange.endDate}`,
          { requiresAuth: true }
        ),
        API.get(
          `sessions?organization=${organizationId}&start=${dateRange.startDate}&end=${dateRange.endDate}`,
          { requiresAuth: true }
        ),
        API.get(
          `stats?organization=${organizationId}&start=${dateRange.startDate}&end=${dateRange.endDate}`,
          { requiresAuth: true }
        ),
      ])
        .then(([insightResponse, sessionResponse, statsResponse]) => {
          if (!isCurrentRequest()) return null;
          const nextInsights = applySortPreference(
            Array.isArray(insightResponse?.data) ? insightResponse.data : [],
            activeInsightSortRef.current,
            "responseCount",
            false
          );
          const nextSessions = applySortPreference(
            Array.isArray(sessionResponse?.data) ? sessionResponse.data : [],
            activeSessionSortRef.current,
            "startTime",
            false
          );
          const nextStatistics =
            statsResponse?.data?.averageResponses !== "NaN" ? statsResponse?.data ?? null : null;

          setInsights(nextInsights);
          setSessions(nextSessions);
          setStatistics(nextStatistics);
          setDataScope(accessScope);
          setDirtyState(false);
          setFetchedAtState(Date.now());

          return {
            insights: nextInsights,
            sessions: nextSessions,
            statistics: nextStatistics,
          };
        })
        .catch((error) => {
          if (isCurrentRequest()) setAnalyticsError(error);
          throw error;
        })
        .finally(() => {
          if (!isCurrentRequest()) return;
          setAnalyticsLoading(false);
          setAnalyticsRefreshing(false);
          inFlightRequestRef.current = null;
        });

      inFlightRequestRef.current = request;
      return request;
    },
    [accessScope, dateRange?.endDate, dateRange?.startDate, organizationId, setDirtyState, setFetchedAtState]
  );

  const scheduleAnalyticsRefresh = useCallback(
    ({ delayMs = 1500, force = false } = {}) => {
      if (!accessScope) return;
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }

      refreshTimerRef.current = window.setTimeout(() => {
        refreshTimerRef.current = null;
        refreshAnalytics({ force }).catch(() => {});
      }, delayMs);
    },
    [accessScope, refreshAnalytics]
  );

  useEffect(() => {
    if (!organizationId || !dateRange?.startDate || !dateRange?.endDate) return;

    markAnalyticsDirty();
    refreshAnalytics({ force: true }).catch(() => {});
  }, [
    dateRange?.endDate,
    dateRange?.startDate,
    organizationId,
    markAnalyticsDirty,
    refreshAnalytics,
  ]);

  useEffect(() => {
    const handleWindowFocus = () => {
      if (dirtyRef.current || !isFresh(lastFetchedAtRef.current)) {
        refreshAnalytics().catch(() => {});
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;

      if (dirtyRef.current || !isFresh(lastFetchedAtRef.current)) {
        refreshAnalytics().catch(() => {});
      }
    };

    window.addEventListener("focus", handleWindowFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleWindowFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshAnalytics]);

  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  const sortSessions = useCallback((attribute, ascending = true) => {
    const nextSort = { attribute, order: ascending };
    activeSessionSortRef.current = nextSort;
    setActiveSessionSort(nextSort);
    setSessions((previousSessions) => sortList(previousSessions, attribute, ascending));
  }, []);

  const sortInsights = useCallback((attribute, ascending = true) => {
    const nextSort = { attribute, order: ascending };
    activeInsightSortRef.current = nextSort;
    setActiveInsightSort(nextSort);
    setInsights((previousInsights) => sortList(previousInsights, attribute, ascending));
  }, []);

  const updateDateRange = useCallback((newDateRange) => {
    if (!newDateRange?.startDate || !newDateRange?.endDate) return;

    setDateRange((previousRange) => {
      if (
        previousRange?.startDate === newDateRange.startDate &&
        previousRange?.endDate === newDateRange.endDate
      ) {
        return previousRange;
      }

      return newDateRange;
    });
  }, []);

  const value = {
    insights: accessScope && dataScope === accessScope ? insights : null,
    sortInsights,
    activeInsightSort,
    sessions: accessScope && dataScope === accessScope ? sessions : null,
    sortSessions,
    activeSessionSort,
    statistics: accessScope && dataScope === accessScope ? statistics : null,
    dateRange,
    updateDateRange,
    refreshAnalytics,
    scheduleAnalyticsRefresh,
    markAnalyticsDirty,
    analyticsDirty,
    lastFetchedAt,
    analyticsLoading,
    analyticsRefreshing,
    analyticsError,
  };

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
};
